import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AlertCircle, AlertTriangle, Bot, CheckCircle2, GitBranch, LogIn, LogOut, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Card, Dialog, ErrorNote, Field, Input, Select, Textarea, Toggle } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { canEdit, rk, useAgents, useMe } from '@/lib/hooks';
import type { AgentDetail, ConfigIssue, FlowConfig, FlowEdge, FlowNode, Version } from '@/lib/types';

type StepData = { step: FlowNode; issues: ConfigIssue[]; agentName?: string };

const ICONS = { input: LogIn, agent: Bot, condition: GitBranch, output: LogOut } as const;
const OPS: Array<{ value: Extract<FlowNode, { type: 'condition' }>['op']; label: string }> = [
  { value: 'truthy', label: 'is not empty' },
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'gt', label: 'is greater than' },
  { value: 'lt', label: 'is less than' },
  { value: 'contains', label: 'contains' },
];

const summary = (s: FlowNode, agentName?: string): string => {
  switch (s.type) {
    case 'input':
      return 'Flow inputs';
    case 'agent':
      return agentName ?? (s.agentSlug || 'Pick an agent');
    case 'condition':
      return `${s.when || '…'} ${OPS.find((o) => o.value === s.op)?.label ?? s.op}${s.op === 'truthy' ? '' : ` ${s.value ?? ''}`}`;
    case 'output':
      return Object.keys(s.outputMap).length ? Object.keys(s.outputMap).join(', ') : 'Last step’s output';
  }
};

/** One canvas node. Conditions expose separate true/false outputs. */
const StepNode = ({ data, selected }: NodeProps<Node<StepData>>) => {
  const { step, issues } = data;
  const Icon = ICONS[step.type];
  const errors = issues.filter((i) => i.level === 'error').length;
  return (
    <div
      className={cn(
        'w-56 rounded-lg border bg-panel px-3 py-2 shadow-sm',
        selected ? 'border-ink ring-2 ring-ink/15' : 'border-line',
        errors > 0 && !selected && 'border-bad/60',
      )}
    >
      {step.type !== 'input' && <Handle type="target" position={Position.Left} className="!size-2.5 !bg-ink-3" />}
      <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">
        <Icon className="size-3.5" />
        {step.type}
        {errors > 0 && <AlertCircle className="ml-auto size-3.5 text-bad" />}
      </div>
      <div className="mt-0.5 truncate text-[13px] font-medium">{step.label || step.id}</div>
      <div className="truncate text-xs text-ink-2">{summary(step, data.agentName)}</div>
      {step.type === 'condition' ? (
        <>
          <Handle id="true" type="source" position={Position.Right} style={{ top: '35%' }} className="!size-2.5 !bg-ok" />
          <Handle id="false" type="source" position={Position.Right} style={{ top: '75%' }} className="!size-2.5 !bg-bad" />
          <span className="absolute top-[28%] -right-8 text-[10px] font-medium text-ok">true</span>
          <span className="absolute top-[68%] -right-9 text-[10px] font-medium text-bad">false</span>
        </>
      ) : (
        step.type !== 'output' && <Handle type="source" position={Position.Right} className="!size-2.5 !bg-ink-3" />
      )}
    </div>
  );
};

const nodeTypes = { step: StepNode };

const newId = (flow: FlowConfig, base: string) => {
  let i = 1;
  while (flow.nodes.some((n) => n.id === `${base}${i}`)) i++;
  return `${base}${i}`;
};

export const FlowBuilder = ({ detail }: { detail: AgentDetail }) => {
  const me = useMe();
  const editable = canEdit(me.data) && !detail.agent.archivedAt;
  const qc = useQueryClient();
  const agents = useAgents();
  const draft = detail.versions.find((v) => v.status === 'draft');
  const live = detail.versions.find((v) => v.id === detail.agent.publishedVersionId);
  const source = draft ?? live ?? detail.versions[0];

  const [flow, setFlow] = useState<FlowConfig>(() => source.config as FlowConfig);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [changelog, setChangelog] = useState(draft?.changelog ?? '');

  useEffect(() => {
    setFlow(source.config as FlowConfig);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.id]);

  const update = (next: FlowConfig) => {
    setFlow(next);
    setDirty(true);
  };
  const updateStep = (id: string, patch: Partial<FlowNode>) => update({ ...flow, nodes: flow.nodes.map((n) => (n.id === id ? ({ ...n, ...patch } as FlowNode) : n)) });

  // Live validation
  const [debounced, setDebounced] = useState(flow);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(flow), 500);
    return () => clearTimeout(t);
  }, [flow]);
  const validation = useQuery({
    queryKey: rk('validate', detail.agent.id, debounced),
    queryFn: () => api<{ issues: ConfigIssue[] }>('/v1/validate', { body: { config: debounced, slug: detail.agent.slug } }),
    placeholderData: (p) => p,
  });
  const issues = validation.data?.issues ?? [];
  const issuesFor = (id: string) => issues.filter((i) => i.path === `nodes.${id}`);

  const agentRows = (agents.data?.agents ?? []).filter((a) => a.kind === 'agent' && a.publishedVersion && a.slug !== detail.agent.slug);
  const agentBySlug = new Map(agentRows.map((a) => [a.slug, a]));

  const nodes: Node<StepData>[] = useMemo(
    () =>
      flow.nodes.map((s) => ({
        id: s.id,
        type: 'step',
        position: s.position,
        selected: s.id === selected,
        data: { step: s, issues: issuesFor(s.id), agentName: s.type === 'agent' ? agentBySlug.get(s.agentSlug)?.name : undefined },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flow.nodes, selected, issues, agents.data],
  );
  const edges: Edge[] = useMemo(
    () =>
      flow.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.branch ?? null,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: e.branch === 'true' ? { stroke: 'var(--color-ok)' } : e.branch === 'false' ? { stroke: 'var(--color-bad)' } : undefined,
        label: e.branch,
      })),
    [flow.edges],
  );

  const onNodesChange = (changes: NodeChange<Node<StepData>>[]) => {
    const moved = applyNodeChanges(changes, nodes);
    const removed = changes.filter((c) => c.type === 'remove').map((c) => c.id);
    const selectChange = changes.find((c) => c.type === 'select' && c.selected);
    if (selectChange && selectChange.type === 'select') setSelected(selectChange.id);
    const hasEdit = changes.some((c) => c.type === 'position' || c.type === 'remove');
    if (!hasEdit) return;
    if (!editable) return;
    const keep = flow.nodes.filter((n) => !removed.includes(n.id) && !(n.type === 'input' && removed.includes(n.id)));
    update({
      ...flow,
      nodes: keep.map((n) => ({ ...n, position: moved.find((m) => m.id === n.id)?.position ?? n.position })),
      edges: flow.edges.filter((e) => !removed.includes(e.source) && !removed.includes(e.target)),
    });
  };
  const onEdgesChange = (changes: EdgeChange[]) => {
    if (!editable) return;
    const next = applyEdgeChanges(changes, edges);
    if (changes.some((c) => c.type === 'remove')) update({ ...flow, edges: flow.edges.filter((e) => next.some((n) => n.id === e.id)) });
  };
  const onConnect = (c: Connection) => {
    if (!editable || !c.source || !c.target || c.source === c.target) return;
    const branch = c.sourceHandle === 'true' || c.sourceHandle === 'false' ? c.sourceHandle : undefined;
    const id = `${c.source}-${c.target}${branch ? `-${branch}` : ''}`;
    if (flow.edges.some((e) => e.id === id)) return;
    const edge: FlowEdge = { id, source: c.source, target: c.target, ...(branch ? { branch } : {}) };
    update({ ...flow, edges: [...flow.edges, edge] });
  };

  const addStep = (type: 'agent' | 'condition' | 'output') => {
    const id = newId(flow, type === 'agent' ? 'step' : type === 'condition' ? 'check' : 'output');
    const maxX = Math.max(0, ...flow.nodes.map((n) => n.position.x));
    const position = { x: maxX + 60, y: 40 + (flow.nodes.length % 4) * 110 };
    const step: FlowNode =
      type === 'agent'
        ? { id, type, position, label: 'New step', agentSlug: agentRows[0]?.slug ?? '', inputMap: {} }
        : type === 'condition'
          ? { id, type, position, label: 'Check', when: '', op: 'truthy' }
          : { id, type, position, label: 'Output', outputMap: {} };
    update({ ...flow, nodes: [...flow.nodes, step] });
    setSelected(id);
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: rk('agent', detail.agent.id) });
    void qc.invalidateQueries({ queryKey: rk('agents') });
  };
  const save = useMutation({
    mutationFn: () => api<{ version: Version }>(`/v1/agents/${detail.agent.id}/draft`, { method: 'PUT', body: { config: flow, changelog } }),
    onSuccess: (r) => {
      setDirty(false);
      invalidate();
      toast.success(`Draft v${r.version.version} saved`);
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const publish = useMutation({
    mutationFn: () => api(`/v1/agents/${detail.agent.id}/versions/${draft!.id}/publish`, { body: { changelog } }),
    onSuccess: () => {
      toast.success(`v${draft!.version} is live`);
      setPublishing(false);
      invalidate();
    },
  });

  const step = flow.nodes.find((n) => n.id === selected) ?? null;
  const errors = issues.filter((i) => i.level === 'error');

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
      <Card
        className="overflow-hidden"
        title={
          <span className="flex items-center gap-2">
            Flow <Badge tone={dirty ? 'warn' : draft ? 'brand' : 'neutral'}>{dirty ? 'unsaved changes' : draft ? `draft v${draft.version}` : live ? `viewing live v${live.version}` : 'new'}</Badge>
          </span>
        }
        actions={
          editable && (
            <>
              <Button size="sm" variant="ghost" onClick={() => addStep('agent')}>
                <Plus className="size-3.5" /> Agent
              </Button>
              <Button size="sm" variant="ghost" onClick={() => addStep('condition')}>
                <Plus className="size-3.5" /> Condition
              </Button>
              <Button size="sm" variant="ghost" onClick={() => addStep('output')}>
                <Plus className="size-3.5" /> Output
              </Button>
              <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
                Save draft
              </Button>
              <Button size="sm" variant="primary" disabled={!draft || dirty || errors.length > 0} onClick={() => setPublishing(true)}>
                Publish…
              </Button>
            </>
          )
        }
      >
        <div className="h-[620px] bg-line-2/40">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onPaneClick={() => setSelected(null)}
            nodesDraggable={editable}
            nodesConnectable={editable}
            deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        <p className="border-t border-line px-4 py-2 text-xs text-ink-3">
          Drag from a step’s right edge to the next step. Conditions have a green (true) and red (false) output. Select a step to configure it; Delete removes it. Independent branches run in parallel.
        </p>
      </Card>

      <div className="flex min-w-0 flex-col gap-5">
        <Card title="Checks">
          <div className="p-4">
            {!issues.length ? (
              <p className={cn('flex items-center gap-1.5 text-xs text-ok', validation.isFetching && 'opacity-50')}>
                <CheckCircle2 className="size-3.5" /> No problems found
              </p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {issues.map((i, n) => (
                  <li key={n}>
                    <button
                      className={cn('flex gap-1.5 text-left text-xs', i.level === 'error' ? 'text-bad' : 'text-warn')}
                      onClick={() => i.path.startsWith('nodes.') && setSelected(i.path.slice(6))}
                    >
                      {i.level === 'error' ? <AlertCircle className="mt-px size-3.5 shrink-0" /> : <AlertTriangle className="mt-px size-3.5 shrink-0" />}
                      {i.message}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
        {step ? (
          <StepInspector
            key={step.id}
            flow={flow}
            step={step}
            editable={editable}
            agents={agentRows}
            onChange={(patch) => updateStep(step.id, patch)}
            onInputsChange={(variables) => update({ ...flow, variables })}
            onDelete={() => {
              update({ ...flow, nodes: flow.nodes.filter((n) => n.id !== step.id), edges: flow.edges.filter((e) => e.source !== step.id && e.target !== step.id) });
              setSelected(null);
            }}
          />
        ) : (
          <Card title="Settings">
            <div className="flex flex-col gap-3 p-4">
              <Field label="Change note">
                <Textarea rows={2} disabled={!editable} value={changelog} onChange={(e) => (setChangelog(e.target.value), setDirty(true))} />
              </Field>
              <Toggle disabled={!editable} checked={flow.logPayloads !== false} onChange={(v) => update({ ...flow, logPayloads: v })} label="Log step inputs and outputs in traces" />
              <p className="text-xs text-ink-3">Select a step on the canvas to configure it.</p>
            </div>
          </Card>
        )}
      </div>

      <Dialog
        open={publishing}
        onClose={() => setPublishing(false)}
        title={`Publish flow v${draft?.version ?? ''}`}
        footer={
          <>
            <Button onClick={() => setPublishing(false)}>Cancel</Button>
            <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
              Publish
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          {flow.nodes.length} steps, {flow.edges.length} connections. {live ? `Replaces live v${live.version}.` : 'First version.'} Hosts pick it up within 30 seconds.
        </p>
        {publish.error ? <div className="mt-3"><ErrorNote error={publish.error} /></div> : null}
      </Dialog>
    </div>
  );
};

/** References a step may use: flow inputs and every step before it. */
const referencesFor = (flow: FlowConfig, stepId: string): string[] => {
  const before = new Set<string>();
  const walk = (id: string) => {
    for (const e of flow.edges.filter((x) => x.target === id)) {
      if (!before.has(e.source)) {
        before.add(e.source);
        walk(e.source);
      }
    }
  };
  walk(stepId);
  return [
    ...(flow.variables ?? []).map((v) => `{{input.${v.name}}}`),
    ...flow.nodes.filter((n) => before.has(n.id) && n.type !== 'input').map((n) => `{{nodes.${n.id}.output}}`),
  ];
};

const RefHints = ({ refs, onPick }: { refs: string[]; onPick?: (r: string) => void }) =>
  refs.length ? (
    <div className="flex flex-wrap gap-1">
      {refs.map((r) => (
        <button key={r} type="button" onClick={() => onPick?.(r)} className="mono rounded bg-brand-soft px-1.5 py-0.5 text-[11px] text-brand hover:bg-brand/15">
          {r}
        </button>
      ))}
    </div>
  ) : (
    <p className="text-xs text-ink-3">Connect this step to earlier steps to use their outputs.</p>
  );

const StepInspector = ({
  flow,
  step,
  editable,
  agents,
  onChange,
  onInputsChange,
  onDelete,
}: {
  flow: FlowConfig;
  step: FlowNode;
  editable: boolean;
  agents: Array<{ slug: string; name: string; variables: Array<{ name: string; required?: boolean; description?: string }> }>;
  onChange: (patch: Partial<FlowNode>) => void;
  onInputsChange: (v: FlowConfig['variables']) => void;
  onDelete: () => void;
}) => {
  const refs = referencesFor(flow, step.id);
  const [focus, setFocus] = useState<string | null>(null);

  return (
    <Card
      title={<span className="capitalize">{step.type} step</span>}
      actions={
        editable &&
        step.type !== 'input' && (
          <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={onDelete} title="Delete step">
            <Trash2 className="size-4" />
          </button>
        )
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Label">
            <Input disabled={!editable} value={step.label ?? ''} onChange={(e) => onChange({ label: e.target.value })} />
          </Field>
          <Field label="Id" hint="for {{nodes.id}}">
            <Input className="mono" disabled value={step.id} />
          </Field>
        </div>

        {step.type === 'input' && (
          <FlowInputs variables={flow.variables ?? []} editable={editable} onChange={onInputsChange} />
        )}

        {step.type === 'agent' && (
          <>
            <Field label="Agent">
              <Select disabled={!editable} value={step.agentSlug} onChange={(e) => onChange({ agentSlug: e.target.value })}>
                <option value="">Pick a published agent…</option>
                {agents.map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.name} ({a.slug})
                  </option>
                ))}
              </Select>
            </Field>
            {(() => {
              const agent = agents.find((a) => a.slug === step.agentSlug);
              if (!agent) return null;
              return (
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium text-ink-2">Variables</div>
                  {agent.variables.length === 0 && <p className="text-xs text-ink-3">This agent takes no variables; use a message below.</p>}
                  {agent.variables.map((v) => (
                    <Field key={v.name} label={<span className="mono">{v.name}{v.required ? ' *' : ''}</span>} hint={v.description}>
                      <Input
                        className="mono"
                        disabled={!editable}
                        value={step.inputMap[v.name] ?? ''}
                        onFocus={() => setFocus(v.name)}
                        onChange={(e) => onChange({ inputMap: { ...step.inputMap, [v.name]: e.target.value } })}
                        placeholder="{{input.…}} or text"
                      />
                    </Field>
                  ))}
                  <Field label="Message" hint="optional user message">
                    <Textarea rows={2} className="mono" disabled={!editable} value={step.message ?? ''} onFocus={() => setFocus('__message')} onChange={(e) => onChange({ message: e.target.value || undefined })} />
                  </Field>
                  <RefHints
                    refs={refs}
                    onPick={(r) => {
                      if (!editable || !focus) return;
                      if (focus === '__message') onChange({ message: `${step.message ?? ''}${r}` });
                      else onChange({ inputMap: { ...step.inputMap, [focus]: `${step.inputMap[focus] ?? ''}${r}` } });
                    }}
                  />
                  <div className="flex flex-col gap-2 border-t border-line-2 pt-3">
                    <Toggle disabled={!editable} checked={Boolean(step.files)} onChange={(v) => onChange({ files: v || undefined })} label="Give this step the files attached to the run" />
                    <Toggle
                      disabled={!editable}
                      checked={Boolean(step.optional)}
                      onChange={(v) => onChange({ optional: v || undefined })}
                      label="If this step fails, skip what depends on it and keep going"
                    />
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {step.type === 'condition' && (
          <>
            <Field label="Test" hint="a value, e.g. {{nodes.cv.output.instances.length}}">
              <Input className="mono" disabled={!editable} value={step.when} onChange={(e) => onChange({ when: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Is">
                <Select disabled={!editable} value={step.op} onChange={(e) => onChange({ op: e.target.value as typeof step.op })}>
                  {OPS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>
              {step.op !== 'truthy' && (
                <Field label="Value">
                  <Input disabled={!editable} value={step.value ?? ''} onChange={(e) => onChange({ value: e.target.value })} />
                </Field>
              )}
            </div>
            <RefHints refs={refs} onPick={(r) => editable && onChange({ when: r })} />
          </>
        )}

        {step.type === 'output' && (
          <OutputMap step={step} refs={refs} editable={editable} onChange={(outputMap) => onChange({ outputMap })} />
        )}
      </div>
    </Card>
  );
};

const FlowInputs = ({ variables, editable, onChange }: { variables: NonNullable<FlowConfig['variables']>; editable: boolean; onChange: (v: FlowConfig['variables']) => void }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-ink-2">Flow inputs</span>
      {editable && (
        <Button size="sm" variant="ghost" onClick={() => onChange([...variables, { name: `input${variables.length + 1}` }])}>
          <Plus className="size-3.5" /> Add
        </Button>
      )}
    </div>
    {variables.length === 0 && <p className="text-xs text-ink-3">What the calling code passes as variables. Steps read them as {'{{input.name}}'}.</p>}
    {variables.map((v, i) => (
      <div key={i} className="flex items-center gap-2">
        <Input className="mono h-7" disabled={!editable} value={v.name} onChange={(e) => onChange(variables.map((x, j) => (j === i ? { ...x, name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') } : x)))} />
        <label className="flex items-center gap-1 text-xs whitespace-nowrap">
          <input type="checkbox" className="accent-ink" disabled={!editable} checked={Boolean(v.required)} onChange={(e) => onChange(variables.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} />
          required
        </label>
        {editable && (
          <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => onChange(variables.filter((_, j) => j !== i))}>
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>
    ))}
  </div>
);

const OutputMap = ({ step, refs, editable, onChange }: { step: Extract<FlowNode, { type: 'output' }>; refs: string[]; editable: boolean; onChange: (m: Record<string, string>) => void }) => {
  const rows = Object.entries(step.outputMap);
  const [focus, setFocus] = useState<string | null>(null);
  const rename = (from: string, to: string) => onChange(Object.fromEntries(rows.map(([k, v]) => [k === from ? to : k, v])));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-2">Result fields</span>
        {editable && (
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...step.outputMap, [`field${rows.length + 1}`]: '' })}>
            <Plus className="size-3.5" /> Add
          </Button>
        )}
      </div>
      {rows.length === 0 && <p className="text-xs text-ink-3">With no fields, the flow returns the output of the step before this one.</p>}
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[110px_minmax(0,1fr)_auto] items-center gap-1.5">
          <Input className="mono h-7" disabled={!editable} defaultValue={k} onBlur={(e) => e.target.value && e.target.value !== k && rename(k, e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))} />
          <Input className="mono h-7" disabled={!editable} value={v} onFocus={() => setFocus(k)} onChange={(e) => onChange({ ...step.outputMap, [k]: e.target.value })} placeholder="{{nodes.…}}" />
          {editable && (
            <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => onChange(Object.fromEntries(rows.filter(([x]) => x !== k)))}>
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
      ))}
      <RefHints refs={refs} onPick={(r) => editable && focus && onChange({ ...step.outputMap, [focus]: `${step.outputMap[focus] ?? ''}${r}` })} />
    </div>
  );
};
